import React from 'react';

import { parseISOToLocale } from '@vlabs/shared/utils/date';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';

import { useGetRole } from '@vlabs/api/artemy/client';
import type { RoleId } from '@vlabs/api/artemy/model';

import { useRoleGeneralInfoTranslation } from './i18n';
import st from './RoleGeneralInfo.module.sass';
import { buildTestid, valueOrHyphen } from '../../../shared/lib/utils';

interface RoleGeneralInfoProps extends WithDataTestid, WithClassNameProp {
  roleId: RoleId;
  actions?: React.ReactNode;
}

const displayName = 'RoleGeneralInfo';
const testid = buildTestid(displayName);

function RoleGeneralInfo({
  roleId,
  actions = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: RoleGeneralInfoProps): React.JSX.Element {
  const { t } = useRoleGeneralInfoTranslation();
  const getRole = useGetRole(roleId);

  const roleName = valueOrHyphen(getRole.data?.name);
  const description = valueOrHyphen(getRole.data?.description);
  const createdAt = valueOrHyphen(parseISOToLocale(getRole.data?.create_time));
  const updatedAt = valueOrHyphen(parseISOToLocale(getRole.data?.update_time));

  return (
    <div
      className={classNames(st.Root, className)}
      {...testid(dataTestId)}
    >
      <div
        className={st.Heading}
        {...testid('Heading', dataTestId)}
      >
        <h6 className={st.Title}>{t('title')}</h6>
        {actions}
      </div>
      <div
        className={st.Data}
        {...testid('Data', dataTestId)}
      >
        <div className={st.Row}>
          <span className={st.Label}>{t('fields.name')}</span>
          <span className={st.Value}>{roleName}</span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span className={st.Label}>{t('fields.description')}</span>
          <span className={classNames(st.Value, st.Value_multiline)}>{description}</span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span className={st.Label}>{t('fields.created')}</span>
          <span className={st.Value}>{createdAt}</span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span className={st.Label}>{t('fields.updated')}</span>
          <span className={st.Value}>{updatedAt}</span>
        </div>
      </div>
    </div>
  );
}

RoleGeneralInfo.displayName = displayName;

export { RoleGeneralInfo };
